import React, { useState, useRef, useEffect, useMemo, useCallback, forwardRef } from 'react';
import { Stethoscope, UserCheck, NotebookTabs, Loader, AlertCircle } from 'lucide-react';
import { usePatient } from '../../context/patientContext';
import { useAuth } from '../../context/authcontext';
import { useHasAnyPermission } from '../../hooks/usePermission';
import { useFeature } from '../../hooks/useFeature';
import { Authorization, AuthorizationsService } from '../../services/patientmr/authorizations.service';
import { PatientAvatar, SamplePill, StatusPill } from '../common';
import Overview from './overview/index';
import Authorizations from './authorizations';
import Referrals from './referrals';
import Insurance from './insurance';
import Charting from './charting/index';
import Demographics from './demographics';
import Medications from './medications';
import CarePlan from './careplan/careplan';
import Assessments from './assessments/assessments';
import Oasis from './oasis';
import CareTeam from './careteam';
import GrowthTracking from './growthtracking';
import VisitHistory from './visithistory';
import Forms from './forms';
import EvvReconciliation from './caseManagement/EvvReconciliation';
import POCCertifications from './pocCertifications';
import OrdersView from './orders/OrdersView';
import PatientDocuments from './documents/PatientDocuments';
import PatientBottomNav from '../layout/PatientBottomNav';
import { useMobileDetection } from '../../hooks/useMobileUtils';
import { useDeviceClass, useFieldMode } from '../../hooks/useOfflineDevice';
import { useNavigate } from 'react-router-dom';
import { visitWorkspacePath } from '../offline/workspace/VisitWorkspaceRoute';
import { Visit } from '../../types/patientmr/visit';
import { StartVisitPayload, getActiveVisit } from '../../services/patientmr/visits.service';
import { useVisitActions } from '../visit/useVisitActions';
import StartVisitSheet, { StartVisitShiftInfo } from '../visit/StartVisitSheet';
import EndVisitSheet, { endVisitErrorMessage } from '../visit/EndVisitSheet';
import { EndVisitSignoff, SUPERVISORY_VISIT_TYPE } from '../../dtos/patientmr/visit.dto';
import { SupervisionService, isSupervisingClinician } from '../../services/patientmr/supervision.service';
import { describeApiError, getApiErrorCode } from '../../utils/apiErrors';
import { eligibleVisitPas, getCurrentCoords, visitLocationPayload } from '../../utils/visitStart';
import { resolveAgencyTimezone, toAgencyDate } from '../../utils/agencyTime';
import { ScheduleService } from '../../services/scheduling';
import { useToast } from '../common/Toast';
import { isApiError } from '../../dtos/common/api.dto';
import PatientLifecycleBar from './lifecycle/PatientLifecycleBar';
import { CoverageSummaryLine, CoverageGapBanner } from './insurance/CoverageSummary';
import { classifyVisitService, decideVisitEvv, isNursingDiscipline, NursingServiceType } from '../../utils/evvRules';
import { usePatientChartSummary, AllergySummary } from './lifecycle/usePatientChartSummary';
import OohDnrBanner from './lifecycle/OohDnrBanner';
import type { PatientData } from '../../dtos/admin/patient.dto';
import PatientScheduleTab from '../scheduling/PatientScheduleTab';
import FamilyPortalTab, { FAMILY_PORTAL_READ } from './familyPortal/FamilyPortalTab';
import type { PermissionString } from '../../types/permissions';
import ProgramBadges from './programs/ProgramBadges';
import { usePatientPrograms } from './programs/usePatientPrograms';

/** Header allergy line (PAT-04/PAT-11): list, "No known allergies", or amber "not recorded". */
const HeaderAllergies: React.FC<{ summary: AllergySummary; compact?: boolean }> = ({ summary, compact = false }) => {
  const size = compact ? 'text-[11px]' : 'text-sm';
  switch (summary.state) {
    case 'list':
      return (
        <span className={`${size} font-medium text-red-700`} data-testid="header-allergies">
          Allergies: {summary.allergens.join(', ')}
        </span>
      );
    case 'none-known':
      return <span className={`${size} text-slate-600`} data-testid="header-allergies">No known allergies</span>;
    case 'not-recorded':
      return (
        <span className={`${size} font-medium text-amber-700`} data-testid="header-allergies">
          Allergies not recorded
        </span>
      );
    default:
      return <span className={`${size} text-slate-400`} data-testid="header-allergies">Allergies: —</span>;
  }
};

interface PatientsProps {
  patientsExpanded: boolean;
  togglePatients: () => void;
  sidebarCollapsed: boolean;
  activeSubsection?: string;
  selectedPatient: any;
  onSidebarSelectionChange?: (section: string) => void;
  setActiveSubsection?: (subsection: string) => void;
  expandedSections?: Record<string, boolean>;
  toggleSection?: (section: string) => void;
  /** Phase 9B: My Day asked to start this shift once the chart is open */
  pendingStart?: { patientId: string; shift: StartVisitShiftInfo } | null;
  onPendingStartHandled?: () => void;
  /** Phase 18U U16: the review queue asked to open this visit's chart */
  pendingReview?: { patientId: string; visitId: string } | null;
  onPendingReviewHandled?: () => void;
}

const Patients = forwardRef(({
  patientsExpanded,
  togglePatients,
  sidebarCollapsed,
  activeSubsection,
  selectedPatient: propSelectedPatient,
  onSidebarSelectionChange,
  setActiveSubsection,
  expandedSections = {},
  toggleSection,
  pendingStart = null,
  onPendingStartHandled,
  pendingReview = null,
  onPendingReviewHandled
}: PatientsProps, ref) => {
  // Get patient data from session context
  const { selectedPatient: contextPatient, patientUnselected, setPatientUnselected } = usePatient();
  
  // Get user data from auth context
  const { user, agencyTimezone } = useAuth();
  const { showToast } = useToast();
  // Use patient from props if provided, otherwise use from context
  const patientData = propSelectedPatient || contextPatient;
  const insuranceInfo = patientData?.insuranceInfo || patientData?.demographics?.insuranceInfo || {};
  // Legacy fallback for patients without an insurance summary yet: mirrors the backend's
  // demographics heuristic (visits.js hasMedicaidCoverage) used when there are no coverage records.
  const coverageLists = [
    (insuranceInfo as any).coverages,
    (patientData as any)?.coverages,
    (patientData as any)?.insuranceCoverages,
    (patientData as any)?.insurances
  ].filter((list): list is any[] => Array.isArray(list));
  const hasMedicaidCoverageRecord = coverageLists.some((coverages) =>
    coverages.some((coverage) => {
      const payerType = String(coverage?.payerType || coverage?.type || '').toLowerCase();
      const provider = String(coverage?.insuranceProvider || coverage?.planName || '').toLowerCase();
      const program = String(coverage?.medicaidProgram || '').toLowerCase();
      return payerType === 'medicaid' || provider.includes('medicaid') || program.length > 0;
    })
  );
  const legacyEvvRequired = Boolean(
    insuranceInfo.medicaidID
    || insuranceInfo.medicaidId
    || insuranceInfo.medicaidIdNumber
    || patientData?.medicaidID
    || patientData?.medicaidId
    || hasMedicaidCoverageRecord
    || [
      insuranceInfo.primaryInsuranceProvider,
      insuranceInfo.secondaryInsuranceProvider,
      insuranceInfo.medicaidProgram,
      insuranceInfo.medicaidMCO,
      insuranceInfo.payerName,
      patientData?.payerName
    ].some((value) => String(value || '').toLowerCase().includes('medicaid'))
  );
  // Responsive design detection
  // Phase 16A: touch phones and tablets get the touch layout at any width; only they start / end visits
  const fieldMode = useFieldMode(true);
  const isMobile = useMobileDetection() || fieldMode;
  const visitActionsHere = useDeviceClass() === 'MOBILE';
  const navigate = useNavigate();
  /** Phase 18U U1: on a phone or tablet a visit is charted on the visit screen (/visit/:visitId) */
  const openVisitScreen = (visitId: string) => {
    if (patientData?.id) navigate(visitWorkspacePath(visitId, patientData.id));
  };
  const isDesktop = !isMobile;
  // Live patient record + header data (Phase 6, PAT-11). Hold/discharge/admit refresh it.
  const canReadDemographicsForHeader = useHasAnyPermission([
    'patientmr:phi:demographics:read' as any,
    'patientmr:phi:demographics:readall' as any
  ]);
  const canReadDashboardForHeader = useHasAnyPermission(['patientmr:phi:dashboard:read' as any]);
  const {
    patient: livePatient,
    setPatient: setLivePatient,
    allergies: headerAllergies,
    dashboard: headerDashboard,
    advanceDirective: headerAdvanceDirective,
    dashboardLoading: headerDashboardLoading,
    dashboardError: headerDashboardError,
    reloadDashboard,
  } = usePatientChartSummary(patientData?.id, {
    canReadDemographics: canReadDemographicsForHeader,
    canReadDashboard: canReadDashboardForHeader,
  });
  const { setSelectedPatient: setContextSelectedPatient, clearSelectedPatient } = usePatient();
  const overviewDashboard = useMemo(
    () => ({ data: headerDashboard, loading: headerDashboardLoading, error: headerDashboardError, refresh: reloadDashboard }),
    [headerDashboard, headerDashboardLoading, headerDashboardError, reloadDashboard]
  );
  const headerStatus: string = String(livePatient?.status || patientData?.status || '').toUpperCase();
  const isPatientOnHold = headerStatus === 'ON_HOLD';
  /** Discharged charts are read-only (DSC-07) */
  const isPatientInactive = livePatient?.status === 'INACTIVE';
  // Phase 7 (REQ-INS-05/06): coverage summary from the server. Its evvRequired is payer-level
  // (Medicaid other than CHIP); the visit's service decides the rest (R-177, below).
  const headerInsuranceSummary = livePatient?.insuranceSummary ?? patientData?.insuranceSummary ?? null;
  const payerEvvRequired = typeof headerInsuranceSummary?.evvRequired === 'boolean' && headerInsuranceSummary.count > 0
    ? headerInsuranceSummary.evvRequired
    : legacyEvvRequired;
  const handlePatientChanged = (updated: PatientData) => {
    setLivePatient(updated);
    setContextSelectedPatient((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
  };
  
  // Helper function to map Dashboard to Overview
  const getActualTabName = (tabName: string) => {
    return tabName === 'Dashboard' ? 'Overview' : tabName;
  };
  
  // State for active tab in desktop view - use activeSubsection if available, otherwise default to 'Overview'
  // Process through getActualTabName to handle Dashboard -> Overview mapping
  const initialActiveTab = activeSubsection ? getActualTabName(activeSubsection) : 'Overview';
  const [activeTab, setActiveTab] = useState(initialActiveTab);
  /** Record to open when a tab is reached from a dashboard alert deep link (R-209) */
  const [tabFocus, setTabFocus] = useState<{ tab: string; id: string } | null>(null);
  const focusIdFor = (tab: string) => (tabFocus && tabFocus.tab === tab ? tabFocus.id : undefined);
  const clearTabFocus = useCallback(() => setTabFocus(null), []);
  
  // State for active category in mobile view
  const [activeMobileCategory, setActiveMobileCategory] = useState<'patientProfile' | 'clinicalCare' | 'management'>('patientProfile');
  
  // State for mobile accordion expansions - track which sections are expanded
  const [mobileExpandedSections, setMobileExpandedSections] = useState<Record<string, boolean>>({});
  
  // Toggle function for mobile accordions
  const toggleMobileSection = (sectionName: string) => {
    setMobileExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };
  
  // Permission checks for tabs visibility
  const canReadCareplan = useHasAnyPermission([
    'patientmr:phi:careplan:read' as any,
    'patientmr:phi:careplan:readall' as any
  ]);

  const canReadAssessment = useHasAnyPermission([
    'patientmr:phi:assessment:read' as any,
    'patientmr:phi:assessment:readall' as any
  ]);

  // Phase 3: OASIS is hidden unless the oasis flag is on (REQ-BIL-12)
  const oasisEnabled = useFeature('oasis');

  const canReadCharting = useHasAnyPermission([
    'patientmr:phi:charting:read' as any,
    'patientmr:phi:charting:readall' as any
  ]);

  // Check if user has permission to read Demographics
  const canReadDemographics = useHasAnyPermission([
    'patientmr:phi:demographics:read' as any,
    'patientmr:phi:demographics:readall' as any
  ]);

  // Check if user has permission to read Medications
  const canReadMedications = useHasAnyPermission([
    'patientmr:phi:medications:read' as any,
    'patientmr:phi:medications:readall' as any
  ]);

  // Check if user has permission to read Care Team
  const canReadProviders = useHasAnyPermission([
    'patientmr:phi:providers:read' as any,
    'patientmr:phi:providers:readall' as any
  ]);

  // Check if user has permission to read Growth Tracking
  const canReadGrowthChart = useHasAnyPermission([
    'patientmr:phi:growthchart:read' as any,
    'patientmr:phi:growthchart:readall' as any
  ]);

  // Check if user has permission to read Dashboard
  const canReadDashboard = useHasAnyPermission([
    'patientmr:phi:dashboard:read' as any
  ]);

  // Phase 8 (§3.8): POC / CMS-485 and Orders under Clinical Care (read:poc)
  const canReadPoc = useHasAnyPermission([
    'patientmr:phi:poc-certification:read' as any,
    'patientmr:phi:poc-certification:readall' as any
  ]);

  // Check if user has permission to read Clinical Care (at least one of the modules)
  const canReadClinicalCare = useHasAnyPermission([
    'patientmr:phi:poc-certification:read' as any,
    'patientmr:phi:poc-certification:readall' as any,
    'patientmr:phi:assessment:read' as any,
    'patientmr:phi:assessment:readall' as any,
    'patientmr:phi:careplan:read' as any,
    'patientmr:phi:careplan:readall' as any,
    'patientmr:phi:charting:read' as any,
    'patientmr:phi:charting:readall' as any
  ]);

  const canReadReferral = useHasAnyPermission([
    'patientmr:phi:referral:read' as any,
    'patientmr:phi:referral:readall' as any
  ]);

  const canReadInsurance = useHasAnyPermission([
    'patientmr:phi:insurance:read' as any,
    'patientmr:phi:insurance:readall' as any
  ]);

  const canReadAuthorization = useHasAnyPermission([
    'patientmr:phi:authorization:read' as any,
    'patientmr:phi:authorization:readall' as any
  ]);

  // Phase 12 (§4.4): the real form read check (was hard-coded true)
  const canReadForm = useHasAnyPermission([
    'patientmr:phi:form:read' as PermissionString,
    'patientmr:phi:form:readall' as PermissionString
  ]);
  // Phase 12: staff Family Portal tab
  const canReadFamilyPortal = useHasAnyPermission([FAMILY_PORTAL_READ]);
  // Phase 10 EVV reconciliation (deep link from the workflow widget)
  const canReadEvvReconciliation = useHasAnyPermission(['evv:reconciliation:read' as PermissionString]);
  // PAT-09: the patient documents library has its own permission family
  const canReadDocuments = useHasAnyPermission([
    'patientmr:phi:documents:read' as any,
    'patientmr:phi:documents:readall' as any
  ]);

  // Check if user has permission to read Case Management (at least one of the four modules)
  const canReadCaseManagement = useHasAnyPermission([
    'patientmr:phi:authorization:read' as any,
    'patientmr:phi:authorization:readall' as any,
    'patientmr:phi:insurance:read' as any,
    'patientmr:phi:insurance:readall' as any,
    'patientmr:phi:poc-certification:read' as any,
    'patientmr:phi:poc-certification:readall' as any,
    'patientmr:phi:referral:read' as any,
    'patientmr:phi:referral:readall' as any,
    'patientmr:phi:form:read' as any,
    'patientmr:phi:form:readall' as any,
    'patientmr:phi:documents:read' as any,
    'patientmr:phi:documents:readall' as any,
    FAMILY_PORTAL_READ
  ]);

  // Check if user has permission to read/create/update/cancel visits
  const canReadVisit = useHasAnyPermission([
    'patientmr:phi:visit:read' as any,
    'patientmr:phi:visit:readall' as any
  ]);

  const canCreateVisit = useHasAnyPermission([
    'patientmr:phi:visit:create' as any,
    'patientmr:phi:visit:createall' as any
  ]);

  const canCancelVisit = useHasAnyPermission([
    'patientmr:phi:visit:cancel' as any
  ]);

  // Phase 9A: patient chart Schedule tab
  const canReadSchedule = useHasAnyPermission([
    'scheduling:phi:read' as any,
    'scheduling:phi:readall' as any
  ]);
  
  // Hierarchical tab structure
  const tabStructure = React.useMemo(() => {
    // Build Clinical Care tabs based on permissions
    const clinicalCareTabs = [];
    if (canReadCharting) {
      clinicalCareTabs.push('Charting');
    }
    if (canReadAssessment) {
      clinicalCareTabs.push('Assessments');
    }
    if (canReadAssessment && oasisEnabled) {
      clinicalCareTabs.push('OASIS');
    }
    if (canReadCareplan) {
      clinicalCareTabs.push('Care Plan');
    }
    if (canReadPoc) {
      clinicalCareTabs.push('POC Certifications');
      clinicalCareTabs.push('Orders');
    }
    
    const categories = [
      {
        category: 'Patient Profile',
        categoryId: 'patientProfile' as const,
        icon: UserCheck,
        tabs: [
          ...(canReadDashboard ? ['Dashboard'] : []),
          ...(canReadSchedule ? ['Schedule'] : []),
          ...(canReadVisit ? ['Visit History'] : []),
          ...(canReadDemographics ? ['Demographics'] : []),
          ...(canReadMedications ? ['Medications'] : []),
          ...(canReadProviders ? ['Care Team'] : []),
          ...(canReadGrowthChart ? ['Growth Tracking'] : [])
        ]
      },
      ...(canReadClinicalCare ? [{
        category: 'Clinical Care',
        categoryId: 'clinicalCare' as const,
        icon: Stethoscope,
        tabs: clinicalCareTabs
      }] : []),
      ...(canReadCaseManagement ? [{
        category: 'Case Management',
        categoryId: 'management' as const,
        icon: NotebookTabs,
        tabs: [
          ...(canReadReferral ? ['Referrals'] : []),
          ...(canReadInsurance ? ['Insurance and Coverage'] : []),
          ...(canReadAuthorization ? ['Prior Authorizations'] : []),
          ...(canReadForm ? ['Forms'] : []),
          ...(canReadDocuments ? ['Documents'] : []),
          ...(canReadFamilyPortal ? ['Family Portal'] : [])
        ]
      }] : [])
    ];
    
    return categories;
  }, [canReadAssessment, oasisEnabled, canReadCareplan, canReadCharting, canReadPoc, canReadClinicalCare, canReadReferral, canReadInsurance, canReadAuthorization, canReadForm, canReadCaseManagement, canReadDocuments, canReadDemographics, canReadMedications, canReadProviders, canReadGrowthChart, canReadDashboard, canReadVisit, canReadSchedule, canReadFamilyPortal]);
  
  // Function to get the provider's discipline in standardized format
  const getProviderDiscipline = () => {
    console.log('=== getProviderDiscipline Debug ===');
    console.log('User object:', user);
    console.log('User exists?', !!user);
    console.log('All user keys:', user ? Object.keys(user) : 'no user');
    
    if (!user) {
      console.log('No user found, defaulting to PT');
      return "PT"; // Default to PT if no user
    }
    
    // Primary: Check user.providerType from Cognito custom attribute
    let providerType = user.providerType;
    
    console.log('Provider type from user.providerType (Cognito):', providerType);
    
    // Fallback: Check firstName and lastName for provider type hints
    // This is a temporary workaround until all users have providerType in Cognito
    if (!providerType && user.firstName && user.lastName) {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
      console.log('No providerType in Cognito - checking full name as fallback:', fullName);
      
      if (fullName.includes('physical therapist') || fullName.includes('pt')) {
        providerType = 'PT';
        console.log('Detected PT from name (fallback)');
      } else if (fullName.includes('occupational therapist') || fullName.includes('ot')) {
        providerType = 'OT';
        console.log('Detected OT from name (fallback)');
      } else if (fullName.includes('speech') || fullName.includes('slp') || fullName.includes('st')) {
        providerType = 'ST';
        console.log('Detected ST from name (fallback)');
      } else if (fullName.includes('nurse') || fullName.includes('rn') || fullName.includes('lvn')) {
        providerType = 'Nurse';
        console.log('Detected Nurse from name (fallback)');
      }
    }
    
    console.log('Final providerType used:', providerType);
    console.log('Provider type exists?', !!providerType);
    
    // If no providerType is set, default to PT
    if (!providerType) {
      console.log('No providerType found in Cognito or name, defaulting to PT');
      return "PT";
    }
    
    console.log('Checking providerType value:', providerType);
    
    // Map provider types to standard discipline codes
    if (providerType.includes('PT') || providerType.includes('Physical')) {
      console.log('Matched PT/Physical -> returning PT');
      return "PT";
    } else if (providerType.includes('OT') || providerType.includes('Occupational')) {
      console.log('Matched OT/Occupational -> returning OT');
      return "OT";
    } else if (providerType.includes('SLP') || providerType.includes('Speech') || providerType.includes('ST')) {
      console.log('Matched SLP/Speech/ST -> returning ST');
      return "ST";
    } else if (
      providerType.includes('RN') ||
      providerType.includes('Nurse')
    ) {
      console.log('Matched RN/Nurse -> returning RN');
      return "RN";
    } else {
      console.log('No match found, returning providerType as-is:', providerType);
      return providerType;
    }
  };
  // Add state for visit management
  const [showStartVisitDialog, setShowStartVisitDialog] = useState(false);
  const [visitActive, setVisitActive] = useState(false);
  const [selectedAuthorization, setSelectedAuthorization] = useState(null);
  const [showEndVisitDialog, setShowEndVisitDialog] = useState(false);
  // Phase 18 M3: the visit just ended stays on the chart for "Review and sign"
  const [reviewVisitId, setReviewVisitId] = useState<string | null>(null);
  const [startEvvClockInFlag, setStartEvvClockInFlag] = useState<'ok' | 'missed_prompt' | ''>('');
  const [endEvvClockOutFlag, setEndEvvClockOutFlag] = useState<'ok' | 'missed_prompt' | ''>('');
  const [currentVisit, setCurrentVisit] = useState<Visit | null>(null);
  // R-177: EVV per visit. RN/LVN pick PDN shift or skilled nursing visit (default from the PA, else PDN)
  const [startServiceType, setStartServiceType] = useState<NursingServiceType | ''>('');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getProviderDiscipline reads only `user`
  const evvDiscipline = useMemo(() => (user ? getProviderDiscipline() : ''), [user]);
  const isNursingVisit = isNursingDiscipline(evvDiscipline);
  const startVisitService = classifyVisitService({
    discipline: evvDiscipline,
    serviceType: startServiceType,
    paServiceCategory: selectedAuthorization?.serviceCategory,
  });
  const startVisitEvv = decideVisitEvv({
    payerEvv: payerEvvRequired,
    evvClass: startVisitService.evvClass,
    collectPdnVisits: user?.evvCollectPdnVisits === true,
  });
  /** HHAX clock-in prompt at visit start */
  const isEvvRequired = startVisitEvv.evvCollect;
  /** HHAX clock-out prompt at visit end: the visit's own snapshot (older visits: evvRequired) */
  const isEvvClockOutRequired = currentVisit
    ? (currentVisit.evvCollect ?? currentVisit.evvRequired ?? payerEvvRequired)
    : isEvvRequired;
  const [isLoadingVisit, setIsLoadingVisit] = useState<boolean>(false);
  const [visitError, setVisitError] = useState<string>('');
  
  // State for tracking provider's active visit across all patients
  const [providerActiveVisit, setProviderActiveVisit] = useState<Visit | null>(null);
  const [hasActiveVisitWithDifferentPatient, setHasActiveVisitWithDifferentPatient] = useState<boolean>(false);
  const hasLoadedVisitStatusRef = useRef<string | null>(null); // Track which patient's visit status has been loaded
  
  // Function to format date from YYYY-MM-DD to MM/DD/YYYY with day as DD
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const [year, month, dayWithTime] = dateStr.split('-');
    // Extract just the day part (handles cases where day might include timestamp)
    const day = dayWithTime.split('T')[0];
    // Pad day with leading zero if needed to ensure DD format
    const formattedDay = day.padStart(2, '0');
    return `${month}/${formattedDay}/${year}`;
  };

  // Function to calculate age from DOB
  const calculateAge = (dobStr: string) => {
    if (!dobStr) return '';
    const dob = new Date(dobStr);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();

    // Adjust age if birthday hasn't occurred yet this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }

    return `${age}y`;
  };

  // ── Phase 9B start / end visit (plan §4, §6.2) ─────────────────────────────────
  const [allAuthorizations, setAllAuthorizations] = useState<Authorization[]>([]);
  const [isLoadingAuthorizations, setIsLoadingAuthorizations] = useState<boolean>(false);
  const [startShift, setStartShift] = useState<StartVisitShiftInfo | null>(null);
  const [startVisitType, setStartVisitType] = useState<'Initial' | 'Evaluation' | ''>('');
  // Phase 18 M13 / F21: an RN may start a supervisory visit when LVN or HHA staff work this patient
  // Phase 20F: header programme badges
  const { programs: headerPrograms } = usePatientPrograms(patientData?.id);
  const [supervisoryAvailable, setSupervisoryAvailable] = useState(false);
  const [startSupervisory, setStartSupervisory] = useState(false);
  const [locationStatus, setLocationStatus] = useState<'locating' | 'ok' | 'unavailable'>('locating');
  const locationRef = useRef<Promise<{ latitude: number; longitude: number } | null> | null>(null);
  const isPatientPending = headerStatus === 'PENDING_ADMISSION';
  const todayAgency = useMemo(() => toAgencyDate(new Date(), resolveAgencyTimezone(agencyTimezone)), [agencyTimezone]);
  const effectiveServiceType: NursingServiceType = startServiceType || (startVisitService.service === 'PDN' ? 'PDN' : 'SKILLED_NURSING_VISIT');
  // PA-14: only PAs the server will accept for this visit
  const availableAuthorizations = useMemo(
    () => eligibleVisitPas(allAuthorizations as never[], { today: todayAgency, discipline: evvDiscipline, serviceType: isNursingVisit ? effectiveServiceType : null }) as unknown as Authorization[],
    [allAuthorizations, todayAgency, evvDiscipline, isNursingVisit, effectiveServiceType]
  );
  useEffect(() => {
    // Keep the choice valid; preselect the only PA
    if (selectedAuthorization && !availableAuthorizations.some((a) => a.id === selectedAuthorization.id)) setSelectedAuthorization(null);
    if (!selectedAuthorization && availableAuthorizations.length === 1) setSelectedAuthorization(availableAuthorizations[0]);
  }, [availableAuthorizations, selectedAuthorization]);

  /** One GPS fix in the background; the start awaits it (fixes the stale-closure 0,0, G13). */
  const captureLocation = () => {
    setLocationStatus('locating');
    const p = getCurrentCoords().then((c) => { setLocationStatus(c ? 'ok' : 'unavailable'); return c; });
    locationRef.current = p;
    return p;
  };

  // Phase 16C: start / end shared with the Offline Visit Workspace (online or queued in the service)
  const visitActions = useVisitActions();

  const visitErrorMessage = (error: unknown, fallback: string) =>
    (isApiError(error) && error.statusCode >= 400 && error.statusCode < 500 && error.message) ? error.message : fallback;

  // Visit management functions
  const handleStartVisitClick = async (shiftForStart?: StartVisitShiftInfo | null) => {
    setSelectedAuthorization(null);
    setVisitError('');
    setStartEvvClockInFlag('');
    setStartVisitType('');
    setStartShift(shiftForStart || null);
    setStartSupervisory(false);
    setSupervisoryAvailable(false);
    if (!patientData || !patientData.id) return;
    if (isPatientOnHold) {
      showToast('This patient is on hold. Resume the patient before starting a visit.', { variant: 'warning' });
      return;
    }
    if (isPatientInactive) {
      showToast('This patient is discharged, so a visit cannot be started.', { variant: 'warning' });
      return;
    }
    captureLocation();
    setShowStartVisitDialog(true);
    if (isSupervisingClinician(user?.providerType)) {
      SupervisionService.patientHasSupervisedStaff(patientData.id, todayAgency, String(user?.providerType || 'RN')).then(setSupervisoryAvailable).catch(() => setSupervisoryAvailable(false));
    }
    setIsLoadingAuthorizations(true);
    try {
      setAllAuthorizations(await AuthorizationsService.getAuthorizations(patientData.id));
    } catch {
      setAllAuthorizations([]);
    } finally {
      setIsLoadingAuthorizations(false);
    }
    if (!shiftForStart) {
      // Show today's shift for this patient if there is one (the server matches it anyway)
      try {
        const mine = await ScheduleService.listByProvider('me', todayAgency, todayAgency);
        const shift = mine.find((s) => s.patientId === patientData.id && s.status === 'SCHEDULED' && !s.visitId);
        if (shift) setStartShift({ id: shift.id, scheduleRef: shift.scheduleRef, startTime: shift.startTime, endTime: shift.endTime, overnight: shift.overnight, visitType: shift.visitType });
      } catch {
        // optional
      }
    }
  };

  // My Day → "Start" opens the chart and then this sheet with the shift
  useEffect(() => {
    if (pendingStart && patientData?.id === pendingStart.patientId) {
      if (visitActionsHere) handleStartVisitClick(pendingStart.shift);
      onPendingStartHandled?.();
    }
  }, [pendingStart, patientData?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirmStartVisit = async () => {
    if (!patientData || !patientData.id) return;
    if (isEvvRequired && !startEvvClockInFlag) {
      setVisitError('Answer the HHAeXchange clock-in question first.');
      return;
    }
    try {
      setIsLoadingVisit(true);
      setVisitError('');
      const coords = locationRef.current ? await locationRef.current : null;
      // A supervisory visit uses no PA (never billed); not offered before admission
      const supervisory = startSupervisory && supervisoryAvailable && !(isPatientPending && !startShift);
      const visitPayload: StartVisitPayload = {
        patientId: patientData.id,
        discipline: getProviderDiscipline(),
        ...visitLocationPayload(coords),
        visitType: supervisory ? SUPERVISORY_VISIT_TYPE : startVisitType || (startShift?.visitType ?? (isPatientPending ? 'Initial' : 'Routine')),
        startTime: new Date().toISOString(),
        notes: '',
        ...(startShift ? { scheduleId: startShift.id } : {}),
        ...(selectedAuthorization && !supervisory ? { authorizationId: String(selectedAuthorization.id), authorizationNumber: selectedAuthorization.authNumber } : {}),
        ...(isEvvRequired && startEvvClockInFlag ? { evvClockInFlag: startEvvClockInFlag } : {}),
        ...(isNursingVisit ? { serviceType: effectiveServiceType } : {})
      };
      const started = await visitActions.start(visitPayload, { patientName: [patientData.firstName, patientData.lastName].filter(Boolean).join(' ') || undefined, evvRequired: isEvvRequired }) as Visit & { warnings?: { code: string; message?: string }[] };
      setShowStartVisitDialog(false);
      setReviewVisitId(null);
      setSelectedAuthorization(null);
      setStartServiceType('');
      setAllAuthorizations([]);
      for (const w of started?.warnings || []) {
        if (w.code === 'HANDOFF_OVERLAP') showToast('Handoff in progress: the previous clinician is still finishing their visit.', { variant: 'info' });
        if (w.code === 'SHIFT_ASSIGNED_TO_OTHER') showToast('This shift was assigned to another clinician; you are recorded as working it.', { variant: 'warning' });
        if (w.code === 'PA_NOT_VALID_FOR_VISIT') showToast('The authorization could not be linked; the visit started unlinked.', { variant: 'warning' });
      }
      await checkProviderVisitStatus();
      const startedId = String(started?.id || (started as { visitId?: string } | null)?.visitId || '');
      if (visitActionsHere && startedId) {
        openVisitScreen(startedId);
        return;
      }
      if (setActiveSubsection) {
        setActiveSubsection('Charting');
        setActiveTab('Charting');
        setActiveMobileCategory('clinicalCare');
      }
    } catch (error) {
      // Phase 18 M13: 403 SUPERVISORY_RN_ONLY has its own text
      setVisitError(getApiErrorCode(error) === 'SUPERVISORY_RN_ONLY'
        ? describeApiError(error, 'The visit could not be started.')
        : visitErrorMessage(error, 'The visit could not be started. Check your connection and try again.'));
    } finally {
      setIsLoadingVisit(false);
    }
  };

  const handleEndVisit = () => {
    setShowEndVisitDialog(true);
    setEndEvvClockOutFlag('');
    setVisitError('');
    captureLocation();
  };
  const handleConfirmEndVisit = async (signoff: EndVisitSignoff) => {
    if (!patientData || !patientData.id) return;
    try {
      setIsLoadingVisit(true);
      setVisitError('');
      let visitToEnd = currentVisit;
      if (!visitToEnd || !visitToEnd.id) visitToEnd = await getActiveVisit(patientData.id);
      const visitId = visitToEnd?.id || visitToEnd?.visitId;
      if (!visitId) {
        setVisitError('No visit in progress was found. Refresh the page and try again.');
        return;
      }
      const coords = locationRef.current ? await locationRef.current : null;
      // Phase 18 M4: signer identity (or why nobody signed) and the no-care reason
      const endData: EndVisitSignoff & { evvClockOutFlag?: 'ok' | 'missed_prompt'; location?: { latitude: number; longitude: number } } = { ...signoff };
      if (isEvvClockOutRequired && endEvvClockOutFlag) endData.evvClockOutFlag = endEvvClockOutFlag;
      const loc = visitLocationPayload(coords);
      if (loc.location) endData.location = loc.location;
      await visitActions.end(patientData.id, visitId, endData);
      setShowEndVisitDialog(false);
      showToast('Visit ended. Review and sign the chart.', { variant: 'success' });
      // Phase 18 M3: stay on this visit's chart to review and submit it
      setReviewVisitId(String(visitId));
      await checkProviderVisitStatus();
      // Phase 18U U1: sign on the visit screen on a phone or tablet
      if (visitActionsHere) {
        openVisitScreen(String(visitId));
        return;
      }
      goToChartingTab();
    } catch (error) {
      setVisitError(endVisitErrorMessage(error, 'The visit could not be ended. Check your connection and try again.'));
    } finally {
      setIsLoadingVisit(false);
    }
  };

  /** Open the Charting tab (desktop tab, or the mobile clinical category) */
  function goToChartingTab() {
    if (!setActiveSubsection) return;
    setActiveSubsection('Charting');
    setActiveTab('Charting');
    setActiveMobileCategory('clinicalCare');
  }

  // A different patient never inherits the "Review and sign" visit
  useEffect(() => { setReviewVisitId(null); }, [patientData?.id]);

  // Use expandedSections from Dashboard or create local state as fallback
  const localExpandedSections = React.useMemo(() => {
    const initialState = {};
    tabStructure.forEach(category => {
      category.tabs.forEach(tab => {
        // Initialize with value from props, or false as default
        initialState[tab] = expandedSections[tab] || false;
      });
    });
    return initialState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps intentional - this should only initialize once on mount
  
  const [localExpandedState] = useState(localExpandedSections);
  
  // Use the Dashboard's expandedSections if available, otherwise use local state
  const effectiveExpandedSections = React.useMemo(() => {
    return Object.keys(expandedSections).length > 0 ? expandedSections : localExpandedState;
  }, [expandedSections, localExpandedState]);
  
  // Create refs for all sections
  const sectionRefs = React.useMemo(() => {
    const refs = {};
    tabStructure.forEach(category => {
      category.tabs.forEach(tab => {
        const actualTab = getActualTabName(tab);
        refs[actualTab] = React.createRef();
      });
    });
    return refs;
  }, [tabStructure]);

  const scrollToSection = (section) => {
    const actualSection = getActualTabName(section);
    // Update the active tab first
    setActiveTab(actualSection);

    // Auto-expand the section in mobile mode and collapse all others
    if (!isDesktop) {
      setMobileExpandedSections({
        [actualSection]: true
      });

      // Determine which category this section belongs to and set active mobile category
      const category = tabStructure.find(cat =>
        cat.tabs.some(tab => getActualTabName(tab) === actualSection)
      );
      if (category) {
        setActiveMobileCategory(category.categoryId);
      }
    }

    // Disable scrollIntoView for mobile - the new accordion layout doesn't need it
    // and it was causing scroll position issues

    // Update sidebar selection if callback is provided
    if (onSidebarSelectionChange) {
      onSidebarSelectionChange(section);
    }
  };
  // Update activeTab when activeSubsection changes
  useEffect(() => {
    if (activeSubsection) {
      const actualSubsection = getActualTabName(activeSubsection);
      // Update the active tab to match the subsection
      setActiveTab(actualSubsection);

      // In mobile mode, auto-expand the section and collapse all others
      if (!isDesktop) {
        setMobileExpandedSections({
          [actualSubsection]: true
        });

        // Determine which category this section belongs to and set active mobile category
        const category = tabStructure.find(cat =>
          cat.tabs.some(tab => getActualTabName(tab) === actualSubsection)
        );
        if (category) {
          setActiveMobileCategory(category.categoryId);
        }
      }

      // Disable automatic scrolling in mobile mode to prevent scroll interference
      // Mobile accordion layout handles scrolling naturally without refs
    }
  }, [activeSubsection, isDesktop, tabStructure]);

  // Define a no-op handleMouseUp to fix the error
  const handleMouseUp = () => {
    // No operation needed, placeholder to satisfy event listener
  };  // Add effect to sync the sidebar selection with the active section on initial mount and when activeTab changes
  useEffect(() => {
    // When component mounts or activeTab changes, update sidebar selection
    if (onSidebarSelectionChange) {
      if (isDesktop) {
        // Map Overview back to Dashboard for sidebar
        const displayTab = activeTab === 'Overview' ? 'Dashboard' : activeTab;
        onSidebarSelectionChange(displayTab);
      } else {
        // On mobile, find the first expanded section
        const expanded = Object.keys(expandedSections).find(section => expandedSections[section]);
        if (expanded) {
          onSidebarSelectionChange(expanded);
        }
      }
    }
  }, [activeTab, effectiveExpandedSections, expandedSections, isDesktop, onSidebarSelectionChange]);

  // Component to render section content
  const renderSectionContent = (sectionName: string) => {
    const actualSection = getActualTabName(sectionName);
    
    // In desktop mode, always expanded. In mobile mode, use accordion state
    const isExpanded = isDesktop || mobileExpandedSections[actualSection];
    const handleToggle = isDesktop ? () => null : () => toggleMobileSection(actualSection);
    
    switch (actualSection) {
      case 'Overview':
        if (!canReadDashboard) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <Overview
              expanded={isExpanded}
              toggleExpanded={handleToggle}
              patientId={patientData?.id}
              patientDob={(livePatient ?? patientData)?.dob}
              readOnly={isPatientInactive}
              // Overview v3 (OV-13): one dashboard request per chart (the header's)
              dashboard={overviewDashboard}
              onNavigateTab={(tab, id) => {
                // Alert deep links carry the record id; the target tab opens that record (R-209)
                setTabFocus(id ? { tab: getActualTabName(tab), id } : null);
                scrollToSection(tab);
              }}
              onNavigate={(path) => {
                // Map WorkflowWidget action paths to patient tab names
                if (path.includes('/schedule'))           return scrollToSection('Schedule');
                if (path.includes('/poc-certifications')) return scrollToSection('POC Certifications');
                if (path.includes('/orders'))             return scrollToSection('Orders');
                if (path.includes('/documents'))          return scrollToSection('Documents');
                if (path.includes('/medications'))        return scrollToSection('Medications');
                if (path.includes('/referrals'))          return setActiveTab('Referrals');
                if (path.includes('/assessments'))        return setActiveTab('Assessments');
                if (path.includes('/care-plans'))         return setActiveTab('Care Plan');
                if (path.includes('/careplans'))          return setActiveTab('Care Plan');
                if (path.includes('/charting'))           return setActiveTab('Charting');
                if (path.includes('/insurance'))          return setActiveTab('Insurance and Coverage');
                if (path.includes('/team'))               return setActiveTab('Care Team');
                if (path.includes('/demographics') || path.includes('/care-team')) return setActiveTab('Demographics');
                if (path.includes('/authorizations'))     return setActiveTab('Prior Authorizations');
                if (path.includes('/evv'))                return setActiveTab('EVV Reconciliation');
                if (path.startsWith('/case-management/prior-authorizations')) return setActiveTab('Prior Authorizations');
                if (path.startsWith('/case-management/evv-reconciliation')) return setActiveTab('EVV Reconciliation');
                if (path.startsWith('/case-management') || path.startsWith('/episode')) return setActiveTab('Prior Authorizations');
                // Fallback: log unhandled paths for future mapping
                console.warn('Unhandled navigation path from WorkflowWidget:', path);
              }}
            />
          </div>
        );
      case 'Schedule':
        if (!canReadSchedule || !patientData?.id) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <PatientScheduleTab
              expanded={isExpanded}
              toggleExpanded={handleToggle}
              patientId={patientData.id}
              patientName={`${patientData?.firstName || ''} ${patientData?.lastName || ''}`.trim() || 'Patient'}
              patientStatus={headerStatus.toLowerCase() || null}
            />
          </div>
        );
      case 'Visit History':
        if (!canReadVisit) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <VisitHistory
              expanded={isExpanded}
              toggleExpanded={handleToggle}
              patientId={patientData?.id}
              canCancelVisit={canCancelVisit}
            />
          </div>
        );
      case 'Charting':
        if (!canReadCharting) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <Charting
              expanded={isExpanded}
              toggleExpanded={handleToggle}
              patientId={patientData?.id}
              activeVisitId={getEffectiveActiveVisitId() || reviewVisitId || undefined}
              activeVisitStatus={!getEffectiveActiveVisitId() && reviewVisitId ? 'DOCPENDING' : undefined}
              onReviewDone={() => setReviewVisitId(null)}
              onOpenVisit={visitActionsHere ? openVisitScreen : undefined}
              openVisitId={pendingReview && pendingReview.patientId === patientData?.id ? pendingReview.visitId : undefined}
              onOpenVisitHandled={onPendingReviewHandled}
            />
          </div>
        );
      case 'OASIS':
        if (!oasisEnabled || !canReadAssessment) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <Oasis
              expanded={isExpanded}
              toggleExpanded={handleToggle}
              patientId={patientData?.id}
              provider={user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email || 'Unknown Provider'}
              activeVisitId={getEffectiveActiveVisitId()}
            />
          </div>
        );
      case 'Care Plan':
        if (!canReadCareplan) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <CarePlan
              expanded={isExpanded}
              toggleExpanded={handleToggle}
              patientId={patientData?.id}
              visitId={getEffectiveActiveVisitId()}
              provider={user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email || 'Unknown Provider'}
            />
          </div>
        );
      case 'Demographics':
        if (!canReadDemographics) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <Demographics
              expanded={isExpanded}
              toggleExpanded={handleToggle}
              patientId={patientData?.id}
              readOnly={isPatientInactive}
            />
          </div>
        );
      case 'Medications':
        if (!canReadMedications) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <Medications
              expanded={isExpanded}
              toggleExpanded={handleToggle}
              patientId={patientData?.id}
              allergies={canReadDemographicsForHeader ? headerAllergies : undefined}
            />
          </div>
        );
      case 'Care Team':
        if (!canReadProviders) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <CareTeam
              expanded={isExpanded}
              toggleExpanded={handleToggle}
            />
          </div>
        );
      case 'Growth Tracking':
        if (!canReadGrowthChart) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <GrowthTracking
              expanded={isExpanded}
              toggleExpanded={handleToggle}
            />
          </div>
        );
      case 'Prior Authorizations':
        if (!canReadAuthorization) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <Authorizations
              expanded={isExpanded}
              toggleExpanded={handleToggle}
              focusId={focusIdFor('Prior Authorizations')}
              onFocusHandled={clearTabFocus}
            />
          </div>
        );
      case 'POC Certifications':
        if (!canReadPoc) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <POCCertifications
              expanded={isExpanded}
              toggleExpanded={handleToggle}
              patientId={patientData?.id}
              focusId={focusIdFor('POC Certifications')}
              onFocusHandled={clearTabFocus}
            />
          </div>
        );
      case 'Orders':
        if (!canReadPoc) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <OrdersView
              expanded={isExpanded}
              toggleExpanded={handleToggle}
              patientId={patientData?.id}
              readOnly={isPatientInactive}
              onOpenPoc={() => scrollToSection('POC Certifications')}
            />
          </div>
        );
      case 'Documents':
        if (!canReadDocuments) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <PatientDocuments
              expanded={isExpanded}
              toggleExpanded={handleToggle}
              patientId={patientData?.id}
              readOnly={isPatientInactive}
              patientDob={(livePatient ?? patientData)?.dob}
              patientName={`${patientData?.firstName || ''} ${patientData?.lastName || ''}`.trim() || undefined}
            />
          </div>
        );
      case 'Referrals':
        if (!canReadReferral) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <Referrals
              expanded={isExpanded}
              toggleExpanded={handleToggle}
              patientId={patientData?.id}
              readOnly={isPatientInactive}
              newReferralDiscipline={focusIdFor('Referrals')?.startsWith('new:') ? focusIdFor('Referrals')!.slice(4) : undefined}
            />
          </div>
        );
      case 'Insurance and Coverage':
        if (!canReadInsurance) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <Insurance
              expanded={isExpanded}
              toggleExpanded={handleToggle}
              patientId={patientData?.id}
            />
          </div>
        );
      case 'Forms':
        if (!canReadForm) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <Forms
              expanded={isExpanded}
              toggleExpanded={handleToggle}
              patientId={patientData?.id}
            />
          </div>
        );
      case 'EVV Reconciliation':
        if (!canReadEvvReconciliation) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <EvvReconciliation
              expanded={isExpanded}
              toggleExpanded={handleToggle}
              patientId={patientData?.id}
            />
          </div>
        );
      case 'Assessments':
        if (!canReadAssessment) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <Assessments
              expanded={isExpanded}
              toggleExpanded={handleToggle}
              patientId={patientData?.id}
              visitId={getEffectiveActiveVisitId()}
              provider={user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email || 'Unknown Provider'}
            />
          </div>
        );
      case 'Family Portal':
        if (!canReadFamilyPortal) return null;
        return (
          <div ref={sectionRefs[actualSection]}>
            <FamilyPortalTab
              expanded={isExpanded}
              toggleExpanded={handleToggle}
              patientId={patientData?.id}
            />
          </div>
        );
      default:
        return <div>Section not found</div>;
    }
  };

  // Add effect to handle patient unselection
  useEffect(() => {
    // If flag is set and patient data is missing, we're in an unselected state
    if (patientUnselected) {
      // When patient is unselected, reset the flag - this only needs to happen once
      // The dashboard component will handle navigation back to the patient dashboard
      setPatientUnselected(false);
    }
  }, [patientUnselected, setPatientUnselected]);
  
  // Effect to ensure the appropriate tab is selected by default when a patient is loaded
  // Only set default tab on initial load, don't change it when visit status changes
  useEffect(() => {
    // If we have a patient and setActiveSubsection is available, set the appropriate default tab
    // Only do this if no subsection is currently active (initial load)
    if (patientData?.id && setActiveSubsection && !activeSubsection) {
      if (visitActive) {
        // Navigate to Charting when visit is active
        setActiveSubsection('Charting');
        setActiveTab('Charting');
        setActiveMobileCategory('clinicalCare');
      } else {
        // Navigate to Overview/Dashboard when no active visit
        setActiveSubsection('Dashboard');
        setActiveTab('Overview');
        setActiveMobileCategory('patientProfile');
      }
    }
  }, [patientData?.id, setActiveSubsection, activeSubsection, visitActive]);
  // Function to check provider's overall visit status
  const checkProviderVisitStatus = async () => {
    try {
      
      if (!patientData?.id) {
        setCurrentVisit(null);
        setVisitActive(false);
        setProviderActiveVisit(null);
        setHasActiveVisitWithDifferentPatient(false);
        return;
      }
      
      // Get provider's active visit (any patient) - only call once
      const providerVisit = await getActiveVisit(patientData.id);
      setProviderActiveVisit(providerVisit);
      
      if (providerVisit) {
        // Check if the active visit is for a different patient
        const isDifferentPatient = providerVisit.patientId !== patientData.id;
        const isSamePatient = providerVisit.patientId === patientData.id;
        setHasActiveVisitWithDifferentPatient(isDifferentPatient);
        
        // If the provider's active visit is for the current patient, set visit as active
        if (isSamePatient) {
          setCurrentVisit(providerVisit);
          setVisitActive(providerVisit.status === 'INPROGRESS');
        } else {
          // Provider has active visit for different patient
          setCurrentVisit(null);
          setVisitActive(false);
        }
      } else {
        // No active visit found for this patient
        setHasActiveVisitWithDifferentPatient(false);
        setCurrentVisit(null);
        setVisitActive(false);
      }
    } catch (error) {
      console.error('Error checking provider visit status:', error);
      // Don't set error state for this as it's a background check
    }
  };

  // Function to determine if visit-dependent data should be shown
  const shouldShowVisitDependentData = () => {
    // Show visit-dependent data if:
    // 1. We have an active visit (visitActive is true)
    // 2. AND the active visit is for the current patient (not a different patient)
    return visitActive && !hasActiveVisitWithDifferentPatient;
  };

  // Function to get the effective active visit ID for charting
  const getEffectiveActiveVisitId = () => {
    // Only return the visit ID if we should show visit-dependent data
    return shouldShowVisitDependentData() ? currentVisit?.id : null;
  };

  // Note: Removed automatic navigation effect to preserve user's current tab when visit status changes

  // Effect to fetch visit status when a patient is loaded
  useEffect(() => {
    // Only fetch if we have a patient ID
    if (patientData?.id) {
      // Skip if already loaded for this patient or if currently loading
      if (hasLoadedVisitStatusRef.current === patientData.id || isLoadingVisit) {
        return;
      }
      
      const checkVisitStatus = async () => {
        setIsLoadingVisit(true);
        hasLoadedVisitStatusRef.current = patientData.id; // Mark as loading immediately to prevent duplicate calls
        try {
          // Check provider's overall visit status - this will set all visit state appropriately
          await checkProviderVisitStatus();
          
        } catch (error) {
          console.error('Error fetching visit status:', error);
          setVisitError('Failed to check visit status. Please refresh the page.');
          hasLoadedVisitStatusRef.current = null; // Reset on error so it can be retried
        } finally {
          setIsLoadingVisit(false);
        }
      };
      
      // Run the check
      checkVisitStatus();
    } else {
      // Reset visit state if no patient
      setCurrentVisit(null);
      setVisitActive(false);
      setProviderActiveVisit(null);
      setHasActiveVisitWithDifferentPatient(false);
      hasLoadedVisitStatusRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientData?.id]); // Only run when patient changes - checkProviderVisitStatus and isLoadingVisit are intentionally excluded to prevent infinite loop

  // Add this useEffect to handle global mouse events
  useEffect(() => {
    if (showEndVisitDialog) {
      // Add global mouse up event listener to stop drawing even if cursor leaves canvas
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [showEndVisitDialog]);  // Start Visit Dialog Component
  const startPatientName = `${patientData?.firstName || ''} ${patientData?.lastName || ''}`.trim() || 'Patient';

  // Use the existing isDesktop check for responsive design
  // Live record once loaded; the list row until then. No placeholder identity is ever shown.
  const headerPatient: { firstName?: string; lastName?: string; dob?: string; mrn?: string } | null =
    livePatient ?? patientData ?? null;

  /** Switch to a chart tab from a lifecycle banner (desktop tab or mobile category + accordion) */
  function navigateToTab(tab: string) {
    const actual = getActualTabName(tab);
    setActiveTab(actual);
    const category = tabStructure.find((c) => c.tabs.some((t) => getActualTabName(t) === actual));
    if (category) setActiveMobileCategory(category.categoryId);
    setMobileExpandedSections((prev) => ({ ...prev, [actual]: true }));
    if (setActiveSubsection) setActiveSubsection(actual === 'Overview' ? 'Dashboard' : actual);
  }

  return (
    <div className="w-full bg-gray-100 min-h-full">
      <div className={`bg-white shadow-lg z-20 w-full border-b border-slate-300 flex-shrink-0`} style={{ position: 'sticky', top: isMobile ? '64px' : '58px', WebkitPosition: 'sticky' } as React.CSSProperties}>
        {/* Mobile-friendly Patient Header */}
        <div className={`flex flex-col sm:flex-row sm:justify-between sm:items-center ${isMobile ? 'py-1.5 px-2' : 'py-4 px-5'}`}>
          <div className="flex items-center w-full">
            {/* Left side: Patient avatar and info */}
            <div className={`flex items-center flex-grow ${isMobile ? 'mr-1' : 'mr-2'}`}>
              {/* Patient avatar */}
              <div className={isMobile ? 'mr-2' : 'mr-3'}>
                <PatientAvatar
                  patientId={patientData?.id || ''}
                  name={`${patientData?.firstName || ''} ${patientData?.lastName || ''}`.trim() || 'Patient'}
                  profilePictureUrl={patientData?.profilePictureUrl}
                  size="lg"
                />
              </div>
              <div className="min-w-0 flex-grow">
                {/* Patient name and essential info */}
                <h1 className={`font-bold truncate text-left text-slate-800 flex items-center gap-2 ${isMobile ? 'text-base leading-tight' : 'text-xl'}`}>
                  <span className="truncate">
                    {(headerPatient?.firstName || headerPatient?.lastName) ?
                      `${headerPatient.firstName || ''} ${headerPatient.lastName || ''}`.trim() : '—'}
                  </span>
                  {headerStatus && <StatusPill status={headerStatus} className="flex-shrink-0" />}
                  {(livePatient?.isSample || patientData?.isSample) && <SamplePill className="flex-shrink-0" />}
                </h1>
                {/* Date of birth - shown on mobile, full info on desktop */}
                {isMobile ? (
                  <div className="text-gray-500 text-xs mt-0.5 text-left">
                    <div>
                      DOB: {headerPatient?.dob ? formatDate(headerPatient.dob) : '—'} ({headerPatient?.dob ? calculateAge(headerPatient.dob) : '—'}) • MRN: {headerPatient?.mrn || '—'}
                    </div>
                    <HeaderAllergies summary={headerAllergies} compact />
                    <CoverageSummaryLine summary={headerInsuranceSummary} />
                  </div>
                ) : (
                <div className={`flex flex-wrap items-center text-gray-500 ${isMobile ? 'gap-x-1.5 text-[11px] mt-0' : 'gap-x-2 text-sm mt-1'}`}>
                  <span>DOB: {headerPatient?.dob ? formatDate(headerPatient.dob) : '—'} ({headerPatient?.dob ? calculateAge(headerPatient.dob) : '—'})</span>
                  <span className="hidden sm:inline text-gray-400">•</span>
                  <span>MRN: {headerPatient?.mrn || '—'}</span>
                  <span className="hidden sm:inline text-gray-400">•</span>
                  <HeaderAllergies summary={headerAllergies} />
                  {visitActive && (
                    <>
                      <span className="hidden sm:inline text-gray-400">•</span>
                      <span className="flex items-center">
                        <span className={`relative flex mr-1 ${isMobile ? 'h-1.5 w-1.5' : 'h-2 w-2'}`}>
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-full w-full bg-green-500"></span>
                        </span>
                        <span className="text-green-700 font-medium">Visit Active</span>
                      </span>
                    </>
                  )}
                </div>
                )}
                {!isMobile && (
                  <div className="mt-0.5 text-left">
                    <CoverageSummaryLine summary={headerInsuranceSummary} />
                  </div>
                )}
              </div>
            </div>

            {/* Right side: Start/End Visit button */}
            {(visitActive || canCreateVisit) && (
            <div className={`flex-shrink-0 sm:ml-0 sm:mt-0 ${isMobile ? 'ml-1' : 'ml-2'}`}>
              {isLoadingVisit ? (
                <button
                  className={`rounded-md bg-gray-400 text-white opacity-50 cursor-not-allowed ${isMobile ? 'px-3 py-2 text-xs min-h-[44px]' : 'px-5 py-2 text-sm'}`}
                  disabled={true}
                >
                  <span className="inline-flex items-center">
                    <Loader size={16} className="animate-spin mr-1.5" />
                    {isMobile ? 'Checking...' : 'Checking Status...'}
                  </span>
                </button>
              ) : visitError ? (
                <div className="flex flex-col items-end">
                  <button
                    className={`rounded-md bg-orange-500 hover:bg-orange-600 text-white ${isMobile ? 'px-3 py-2 text-xs min-h-[44px]' : 'px-5 py-2 text-sm'}`}
                    onClick={() => {
                      // Clear error and retry fetching visit status
                      setVisitError('');
                      if (patientData?.id) {
                        const checkVisitStatus = async () => {
                          setIsLoadingVisit(true);
                          try {
                            const currentVisitData = await getActiveVisit(patientData.id);
                            if (currentVisitData) {
                              setCurrentVisit(currentVisitData);
                              const isActive = currentVisitData.status === 'INPROGRESS';
                              setVisitActive(isActive);
                            } else {
                              setCurrentVisit(null);
                              setVisitActive(false);
                            }
                          } catch (error) {
                            console.error('Error retrying visit status check:', error);
                            setVisitError('Failed to check visit status. Please refresh the page.');
                          } finally {
                            setIsLoadingVisit(false);
                          }
                        };
                        checkVisitStatus();
                      }
                    }}
                  >
                    Retry
                  </button>
                  <span className="text-xs text-red-600 mt-1">Status check failed</span>
                </div>
              ) : !visitActionsHere ? (
                // Design §2.1: visits start and end (GPS, EVV prompt, signature) on a phone or tablet only
                <span className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700" role="note">
                  {visitActive && <span className="inline-flex h-2 w-2 rounded-full bg-green-500" aria-hidden="true" />}
                  {visitActive ? 'Visit in progress · end it on your phone or tablet' : 'Start visits on your phone or tablet'}
                </span>
              ) : (
                <button
                  className={`rounded-md transition-colors flex items-center justify-center font-medium ${isMobile ? 'px-3 py-2 text-xs min-h-[44px] min-w-[85px]' : 'px-5 py-2 text-sm'} ${
                    visitActive
                      ? 'bg-slate-800 hover:bg-slate-500 text-white ring-2 ring-green-500 ring-offset-1'
                      : hasActiveVisitWithDifferentPatient || isLoadingAuthorizations || isPatientOnHold
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-slate-800 hover:bg-slate-700 text-white'
                  }`}
                  onClick={visitActive ? handleEndVisit : () => handleStartVisitClick()}
                  disabled={!patientData || hasActiveVisitWithDifferentPatient || isLoadingAuthorizations || isPatientOnHold}
                >
                  {visitActive && (
                    <span className={`relative flex mr-1.5 ${isMobile ? 'h-2 w-2' : 'h-2 w-2'}`}>
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-full w-full bg-green-500"></span>
                    </span>
                  )}
                  {isLoadingAuthorizations && !visitActive && <Loader size={16} className="animate-spin mr-1.5" />}
                  {visitActive ? 'End Visit' : isPatientOnHold ? 'Patient On Hold' : isLoadingAuthorizations ? 'Loading...' : 'Start Visit'}
                </button>
              )}
            </div>
            )}
          </div>        </div>
        <OohDnrBanner advanceDirective={headerAdvanceDirective} compact={isMobile} />
        {/* Phase 20F: palliative care programme and hospice election */}
        <ProgramBadges programs={headerPrograms} compact={isMobile} />
      </div>
      
      {headerInsuranceSummary && (
        <div className="px-3 sm:px-5 pt-2">
          <CoverageGapBanner summary={headerInsuranceSummary} onOpenInsurance={() => navigateToTab('Insurance and Coverage')} />
        </div>
      )}

      <PatientLifecycleBar
        patient={livePatient}
        dashboard={headerDashboard}
        onPatientChanged={handlePatientChanged}
        onNavigateTab={navigateToTab}
        onSampleDeleted={clearSelectedPatient}
      />

      {/* Mobile accordion styling, scoped to this chart (G16: it used to override .p-3, .gap-4,
          .mb-4 … for the whole app, shrinking sheets and touch targets) */}
      {isMobile && (
        <style>{`
          @media (max-width: 767px) {
            .patient-chart-mobile .bg-white.rounded.shadow { border-radius: 0; box-shadow: none; border-bottom: 2px solid #E2E8F0; }
            .patient-chart-mobile .space-y-0 > * { margin-top: 0; margin-bottom: 2px; border-bottom: 2px solid #CBD5E1; }
            .patient-chart-mobile .space-y-0 > *:last-child { border-bottom: none; }
          }
        `}</style>
      )}
      
      {/* Content area */}
      <div className={`${isMobile ? 'p-0 patient-chart-mobile' : 'p-4'}`}>
        {/* Display only errors, no visit information banners */}
        {isLoadingVisit && (
          <div className={`flex items-center bg-slate-50 border border-slate-200 p-3 ${isMobile ? 'rounded-none mb-0' : 'rounded-md mb-4'}`}>
            <Loader size={16} className="animate-spin mr-2 text-slate-500" />
            <p className="text-slate-700">Loading visit information...</p>
          </div>
        )}
        
        {visitError && !isLoadingVisit && (
          <div className={`bg-red-50 border border-red-100 p-3 ${isMobile ? 'rounded-none mb-0' : 'rounded-md mb-4'}`}>
            <p className="text-red-800">{visitError}</p>
          </div>
        )}

        {/* Show information banner when there's an active visit with a different patient */}
        {hasActiveVisitWithDifferentPatient && providerActiveVisit && 
          // Only show the banner for Charting, Assessments, and Care Plan tabs
          (activeTab === 'Charting' || activeTab === 'Assessments' || activeTab === 'Care Plan') && (
            <div className={`bg-amber-50 border border-amber-200 p-3 ${isMobile ? 'rounded-none mb-0' : 'rounded-md mb-4'}`}>
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-amber-800">Active Visit in Progress</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    You have a visit in progress with {(providerActiveVisit as { patientName?: string }).patientName || 'another patient'}.
                    End it (tap the green bar at the top) before starting a visit with this patient.
                  </p>
                </div>
              </div>
            </div>
          )}

        {/* Patient sections with new hierarchical structure */}
        {isDesktop ? (
          <div className="mb-4">
            {/* Desktop two-tier tab structure */}
            <div className="bg-white rounded-lg shadow-sm">
              {/* Primary tabs row */}
              <div className="px-2 pt-2 pb-0">
                <div className="flex space-x-1">
                  {tabStructure.map((category) => {
                    // Check if any tab in this category is active
                    const isCategoryActive = category.tabs.some(tab => {
                      const actualTab = getActualTabName(tab);
                      return activeTab === actualTab;
                    });
                    
                    return (
                      <button
                        key={category.category}
                        onClick={() => {
                          // When clicking a primary tab, activate the first secondary tab in that category
                          const firstTab = category.tabs[0];
                          const actualFirstTab = getActualTabName(firstTab);
                          setActiveTab(actualFirstTab);
                          if (onSidebarSelectionChange) onSidebarSelectionChange(firstTab);
                          if (setActiveSubsection) setActiveSubsection(firstTab);
                        }}
                        className={`px-4 py-2 text-sm font-medium rounded-t-md transition-all duration-200 flex items-center space-x-1.5
                          ${isCategoryActive
                            ? 'bg-slate-800 text-white'
                            : 'bg-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-800'
                          }`}
                      >
                        <category.icon size={16} strokeWidth={2.5} />
                        <span>{category.category}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Secondary tabs row */}
              <div className="px-2 py-2 bg-slate-50">
                <div className="flex flex-wrap gap-1.5">
                  {tabStructure.map((category) => {
                    // Check if this category is active
                    const isCategoryActive = category.tabs.some(tab => {
                      const actualTab = getActualTabName(tab);
                      return activeTab === actualTab;
                    });
                    
                    // Only show secondary tabs for the active category
                    if (!isCategoryActive) return null;
                    
                    return category.tabs.map((tab) => {
                      const actualTab = getActualTabName(tab);
                      return (
                        <button
                          key={tab}
                          onClick={() => {
                            setActiveTab(actualTab);
                            scrollToSection(actualTab);
                            if (onSidebarSelectionChange) onSidebarSelectionChange(tab);
                            if (setActiveSubsection) setActiveSubsection(tab);
                          }}
                          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200
                            ${activeTab === actualTab
                              ? 'bg-slate-800 text-white'
                              : 'bg-white text-slate-700 hover:bg-slate-100'
                            }`}
                        >
                          {tab}
                        </button>
                      );
                    });
                  })}
                </div>
              </div>
            </div>

            {/* Render section content based on active tab - no wrapper needed, component has its own */}
            <div key={activeTab}>
              {renderSectionContent(activeTab)}
            </div>
          </div>
        ) : (
          <div>
            {/* Mobile view - show only active category as simple list with component accordions */}
            <div className="mobile-profile-sections flex flex-col gap-3 pt-3 pb-20 px-3">
              {tabStructure
                .filter((category) => category.categoryId === activeMobileCategory)
                .map((category) => (
                  <React.Fragment key={category.category}>
                    {category.tabs.map((tab) => {
                      return (
                        <div key={tab} className="rounded-lg shadow-md overflow-hidden">
                          {renderSectionContent(tab)}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))
              }
            </div>
            
            {/* Bottom Navigation for Mobile */}
            <PatientBottomNav
              activeCategory={activeMobileCategory}
              onCategoryChange={(category) => {
                setActiveMobileCategory(category);
                // Optionally, set the first tab of the new category as active
                const newCategory = tabStructure.find(c => c.categoryId === category);
                if (newCategory && newCategory.tabs.length > 0) {
                  const firstTab = newCategory.tabs[0];
                  const actualFirstTab = getActualTabName(firstTab);
                  setActiveTab(actualFirstTab);
                  if (setActiveSubsection) setActiveSubsection(firstTab);
                }
              }}
              hasPatient={!!patientData}
              sidebarCollapsed={sidebarCollapsed}
              canReadClinicalCare={canReadClinicalCare}
              canReadCaseManagement={canReadCaseManagement}
              alwaysShow={fieldMode}
            />
          </div>
        )}
      </div>

      {canCreateVisit && (
        <StartVisitSheet
          open={showStartVisitDialog}
          onClose={() => setShowStartVisitDialog(false)}
          patientName={startPatientName}
          shift={startShift}
          pendingAdmission={isPatientPending && !startShift}
          visitType={startVisitType}
          onVisitTypeChange={setStartVisitType}
          pas={availableAuthorizations}
          loadingPas={isLoadingAuthorizations}
          selectedPaId={selectedAuthorization ? String(selectedAuthorization.id) : null}
          onSelectPa={(id) => setSelectedAuthorization(availableAuthorizations.find((a) => String(a.id) === id) || null)}
          isNursing={isNursingVisit}
          serviceType={effectiveServiceType}
          onServiceTypeChange={(v) => { setStartServiceType(v); setStartEvvClockInFlag(''); }}
          evvPrompt={isEvvRequired}
          evvRequired={startVisitEvv.evvRequired}
          clockIn={startEvvClockInFlag}
          onClockIn={setStartEvvClockInFlag}
          location={{ status: locationStatus }}
          supervisoryAvailable={supervisoryAvailable}
          supervisory={startSupervisory}
          onSupervisoryChange={setStartSupervisory}
          error={visitError || null}
          busy={isLoadingVisit}
          onConfirm={handleConfirmStartVisit}
        />
      )}
      <EndVisitSheet
        open={showEndVisitDialog}
        onClose={() => setShowEndVisitDialog(false)}
        patientName={startPatientName}
        startedAt={currentVisit?.startTime || null}
        patientId={patientData?.id || null}
        visitId={currentVisit?.id || currentVisit?.visitId || null}
        onReviewChart={() => { setShowEndVisitDialog(false); goToChartingTab(); }}
        evvPrompt={Boolean(isEvvClockOutRequired)}
        clockOut={endEvvClockOutFlag}
        onClockOut={setEndEvvClockOutFlag}
        error={visitError || null}
        busy={isLoadingVisit}
        onConfirm={handleConfirmEndVisit}
      />
    </div>
  );
});

export default Patients;